import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Stack,
  Grid,
  Alert,
  Skeleton,
  CssBaseline,
  Container,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  DisabledVisibleOutlined
} from '@mui/icons-material';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer
} from '@mui/material';

import AppNavbar from '../../../components/AppNavbar';
import Header from '../../../components/Header';
import SideMenu from '../../../components/SideMenu';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../dashboard/theme/customizations';
import AppTheme from '../../../shared-theme/AppTheme';
import { StyledCellHeader } from '../../../components/StyledCellHeader';

import CrearEditarCategoriaDialog from './components/CrearEditarCategoriaDialog';
import OnGeneralMessageDialog from '../../../components/Dialogs/OnGeneralMessageDialog';
import AccessDeniedBox from '../../../components/AccessDeniedBox';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};




const CategoriasCargosScreen = (props) => {
  // State management
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  // Filters state
  const [filters, setFilters] = useState({
    search: ''
  });
  
  // Dialog states
  const [showCrearDialog, setShowCrearDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Selected items
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [deletingCategoria, setDeletingCategoria] = useState(null);


  const [isDeleting, setIsDeleting] = useState(false);
  

  
  // Load data
  useEffect(() => {
    loadCategorias();
  }, []);
  
  const loadCategorias = async () => {
    const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/categorias_cargos/company/${localStorage.getItem('idcompany')}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Usuario-ID': localStorage.getItem('id'),
        'X-company-ID': localStorage.getItem('idcompany')
      }
    });
    const data = await response.json();
    if(data["error"]!="Acceso denegado"){
      setAccessDenied(false);
      setCategorias(data);
      setLoading(false);  
    }else{
      setAccessDenied(true);
      setLoading(false);
    }
  };
  
  // Filtered data
  const filteredCategorias = useMemo(() => {
    return categorias.filter(categoria => {
      const matchesSearch = !filters.search || 
        categoria.nombre.toLowerCase().includes(filters.search.toLowerCase());
      
      return matchesSearch;
    });
  }, [categorias, filters]);
  

  
  // Event handlers
  
  const handleEdit = (categoria) => {
    setEditingCategoria(categoria);
    setShowCrearDialog(true);
  };
  
  const handleToggleStatus = (categoria) => {
    setDeletingCategoria(categoria);
    setShowConfirmDialog(true);
    setIsDeleting(false);
  };
  
  const handleDelete = (categoria) => {
    setDeletingCategoria(categoria);
    setIsDeleting(true);
    setShowConfirmDialog(true);
  };
  
  const handleClearFilters = () => {
    setFilters({
      search: ''
    });
  };
  
  const handleCrearSuccess = (nuevaCategoria) => {
    setCategorias(prev => [...prev, nuevaCategoria]);
    setShowCrearDialog(false);
    setEditingCategoria(null);
  };
  
  const handleEditarSuccess = (categoriaEditada) => {
    setCategorias(prev => prev.map(cat => 
      cat.id === categoriaEditada.id ? categoriaEditada : cat
    ));
    setShowCrearDialog(false);
    setEditingCategoria(null);
  };
  
  const handleConfirmAction = async () => {
    
    try {   
      if(!isDeleting){
        var response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/categorias_cargos/${deletingCategoria.id}/toggle-status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          }
        });
        const data = await response.json();
        setCategorias(prev => prev.map(cat => 
          cat.id === deletingCategoria.id 
            ? { ...cat, activo: !cat.activo }
            : cat
        ));
      }else{
        var response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/categorias_cargos/${deletingCategoria.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          }
        });
        setCategorias(prev => prev.filter(cat => cat.id !== deletingCategoria.id));
      }
      
      setShowConfirmDialog(false);
      setDeletingCategoria(null);
    } catch (err) {
      setError('Error al procesar la acción');
      console.error('Error processing action:', err);
    }
  };
  
  // Loading skeleton
  if (loading) {
    return (
      <AppTheme {...props} themeComponents={xThemeComponents}>
        <CssBaseline enableColorScheme />
        <Box sx={{ display: 'flex' }}>
          <SideMenu />
          <AppNavbar />
          <Box
            component="main"
            sx={(theme) => ({
              flexGrow: 1,
              backgroundColor: theme.vars
                ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                : alpha(theme.palette.background.default, 1),
              overflow: 'auto',
            })}
          >
            <Stack
              spacing={2}
              sx={{
                alignItems: 'center',
                mx: 3,
                pb: 5,
                mt: { xs: 8, md: 0 },
              }}
            >
              <Header parent={"Gestión Comercial"} child={"Categorías de Cargos"} />
              <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
                <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
                  <Box maxWidth="lg" mx="auto" py={4}>
                    <Box sx={{ p: 3 }}>
                      <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                      <Skeleton variant="rectangular" height={400} />
                    </Box>
                  </Box>
                </Box>
              </Container>
            </Stack>
          </Box>
        </Box>
      </AppTheme>
    );
  }
  
  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header parent={"Gestión Comercial"} child={"Categorías de Cargos"} />
            {!accessDenied ? 
            <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
              <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
                <Box maxWidth="lg" mx="auto" py={4}>
                  {/* Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h4" fontWeight={600}>
                      Categorías de cargos
                    </Typography>
                    <Button
                      color='primarybutton'
                      sx={{fontWeight:"bold"}}
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setShowCrearDialog(true)}
                    >
                      Nueva categoría
                    </Button>
                  </Stack>
      
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item size={10}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item size={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              fullWidth
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, boxShadow: 'none' }}>
        {filteredCategorias.length === 0 ? (
          <Box sx={{ 
            height: 400, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            p: 3
          }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No se encontraron categorías
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filters.search
                ? 'Intenta ajustar los filtros'
                : 'Crea tu primera categoría de cargo'
              }
            </Typography>
            {!filters.search && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{fontWeight: 600}}
                color='primarybutton'
                onClick={() => setShowCrearDialog(true)}
              >
                Crear categoría
              </Button>
            )}
          </Box>
        ) : (
          <TableContainer sx={{borderRadius: 2}}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledCellHeader>Nombre</StyledCellHeader>
                  <StyledCellHeader>Aplica a</StyledCellHeader>
                  <StyledCellHeader>Tipo</StyledCellHeader>
                  <StyledCellHeader>Descripción</StyledCellHeader>
                  <StyledCellHeader>Gestión operativa</StyledCellHeader>
                  <StyledCellHeader>Monto</StyledCellHeader>
                  <StyledCellHeader>Estado</StyledCellHeader>
                  <StyledCellHeader>Acciones</StyledCellHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCategorias.map((categoria) => (
                  <TableRow key={categoria.id}>
                    <TableCell>{categoria.nombre}</TableCell>
                    <TableCell>
                      <Chip
                        label={categoria.aplicaA === 'producto' ? 'Producto' : 'Préstamo'}
                        color={categoria.aplicaA === 'producto' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {categoria.aplicaA === 'prestamo' ? (
                        <Chip
                          label={categoria.tipo === 'especifico' ? 'Específico' : 'General'}
                          color={categoria.tipo === 'especifico' ? 'warning' : 'default'}
                          size="small"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                        <TableCell>{categoria.descripcion}</TableCell>
                    <TableCell>
                      <Chip 
                        label={categoria.requiereGestionOperativa ? 'Sí' : 'No'}
                        color={categoria.requiereGestionOperativa ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={categoria.montoFijo}
                        color="default"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={categoria.activo ? 'Activo' : 'Inactivo'}
                        color={categoria.activo ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton 

                          size="small" 
                          onClick={() => handleEdit(categoria)}
                          title="Ver"
                        >
                          <ViewIcon fontSize="small" color='#FFC107' />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleToggleStatus(categoria)}
                          title={categoria.activo ? 'Desactivar' : 'Activar'}
                        >
                          {categoria.activo ? <DisabledVisibleOutlined fontSize="small" color='#aeb0af' /> : <ActiveIcon fontSize="small" color='success' />}
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(categoria)}
                          title="Eliminar"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      {/* Dialogs */}
      <CrearEditarCategoriaDialog
        open={showCrearDialog}
        onClose={() => {
          setShowCrearDialog(false);
          setEditingCategoria(null);
        }}
        onSuccess={editingCategoria ? handleEditarSuccess : handleCrearSuccess}
        categoria={editingCategoria}
      />
      
      
      <OnGeneralMessageDialog
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setDeletingCategoria(null);
        }}
        onRegister={handleConfirmAction}
        title={!isDeleting ? 'Cambiar estado' : 'Eliminar categoría'}
        message={
          !isDeleting
            ? `¿Estás seguro de que quieres ${deletingCategoria?.activo ? 'desactivar' : 'activar'} la categoría "${deletingCategoria?.nombre}"?`
            : `¿Estás seguro de que quieres eliminar la categoría "${deletingCategoria?.nombre}"? Esta acción no se puede deshacer.`
        }
        buttonText={!isDeleting ? 'Confirmar' : 'Eliminar'}
        buttonColor={!isDeleting ? 'primarybutton' : 'error'}
      />
                </Box>
              </Box>
            </Container>
            :
            <Box>
              <br></br>
              <br></br>
            <AccessDeniedBox
              title={'Acceso denegado'}
              message={'No tienes permisos para ver esta sección de Categorías de Cargos. Contacta a tu administrador para solicitar acceso o verifica tus roles y permisos.'}
            />
            </Box>
            }
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
};

export default CategoriasCargosScreen;
